// Tool registry. Each tool has UI metadata + an optional executor.
// Tools without an executor are display-only stubs; tools with one
// can be invoked live by adapters via runTool().

export interface ToolExecutionContext {
  signal?: AbortSignal;
}

export type ToolExecutor = (
  input: unknown,
  ctx: ToolExecutionContext
) => Promise<unknown>;

export interface ToolDefinition {
  name: string;
  label: string;
  category: 'fs' | 'shell' | 'web' | 'analysis' | 'agent' | 'misc';
  inputPreview?: (input: unknown) => string;
  /** if true, the UI defaults to "requires_approval" for first-call */
  sensitive?: boolean;
  /** if present, this tool is real and can be invoked. Without one, it's a stub. */
  execute?: ToolExecutor;
  /** human description shown in /tools */
  description?: string;
}

// ----- real executors -----

const nowExecutor: ToolExecutor = async () => {
  const d = new Date();
  return {
    iso: d.toISOString(),
    locale: d.toLocaleString(),
    epochMs: d.getTime(),
  };
};

const clipboardReadExecutor: ToolExecutor = async () => {
  if (!navigator.clipboard?.readText) {
    throw new Error('Clipboard API unavailable in this environment.');
  }
  const text = await navigator.clipboard.readText();
  return { text, length: text.length };
};

const clipboardWriteExecutor: ToolExecutor = async (input) => {
  const text = typeof (input as { text?: unknown })?.text === 'string'
    ? (input as { text: string }).text
    : '';
  if (!text) throw new Error('clipboard_write requires { text: string }');
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard API unavailable in this environment.');
  }
  await navigator.clipboard.writeText(text);
  return { written: text.length };
};

const fetchUrlExecutor: ToolExecutor = async (input, ctx) => {
  const url = (input as { url?: string })?.url;
  if (!url || typeof url !== 'string') throw new Error('fetch_url requires { url: string }');
  const res = await fetch(url, { signal: ctx.signal });
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    const json = await res.json();
    return { status: res.status, contentType: ct, json };
  }
  const text = await res.text();
  return {
    status: res.status,
    contentType: ct,
    text: text.length > 4096 ? text.slice(0, 4096) + '\n…[truncated]' : text,
    truncated: text.length > 4096,
  };
};

// ----- registry -----

export const TOOLS: Record<string, ToolDefinition> = {
  // metadata-only stubs (need a real backend or Tauri to wire up)
  read_file: {
    name: 'read_file', label: 'Read file', category: 'fs',
    inputPreview: (i: any) => i?.path ?? '',
    description: 'Read a file from the workspace (requires Tauri or backend).',
  },
  write_file: {
    name: 'write_file', label: 'Write file', category: 'fs', sensitive: true,
    inputPreview: (i: any) => i?.path ?? '',
    description: 'Write to a file (requires Tauri or backend).',
  },
  run_command: {
    name: 'run_command', label: 'Run command', category: 'shell', sensitive: true,
    inputPreview: (i: any) => i?.cmd ?? '',
    description: 'Run a shell command (requires Tauri).',
  },
  run_tests: {
    name: 'run_tests', label: 'Run tests', category: 'shell',
    inputPreview: (i: any) => i?.target ?? 'all',
    description: 'Run the project test suite (requires Tauri).',
  },
  grep: {
    name: 'grep', label: 'Search code', category: 'fs',
    inputPreview: (i: any) => i?.pattern ?? '',
    description: 'Search the workspace by pattern (requires Tauri).',
  },
  web_search: {
    name: 'web_search', label: 'Web search', category: 'web',
    inputPreview: (i: any) => i?.query ?? '',
    description: 'Search the web (requires search provider key).',
  },
  read_url: {
    name: 'read_url', label: 'Read URL', category: 'web',
    inputPreview: (i: any) => i?.url ?? '',
    description: 'Fetch and extract readable text from a URL (limited by CORS).',
  },
  browser: {
    name: 'browser', label: 'Browser', category: 'web',
    description: 'Drive a browser (requires Claude-in-Chrome or Tauri webview).',
  },

  // real, browser-side tools
  now: {
    name: 'now', label: 'Current time', category: 'misc',
    description: 'Returns the current timestamp.',
    execute: nowExecutor,
  },
  clipboard_read: {
    name: 'clipboard_read', label: 'Read clipboard', category: 'misc',
    description: 'Reads text from the system clipboard.',
    execute: clipboardReadExecutor,
  },
  clipboard_write: {
    name: 'clipboard_write', label: 'Write clipboard', category: 'misc',
    description: 'Writes text to the system clipboard.',
    inputPreview: (i: any) => (typeof i?.text === 'string' ? i.text.slice(0, 40) : ''),
    execute: clipboardWriteExecutor,
  },
  fetch_url: {
    name: 'fetch_url', label: 'Fetch URL', category: 'web',
    inputPreview: (i: any) => i?.url ?? '',
    description: 'Fetches a URL via browser fetch (subject to CORS).',
    execute: fetchUrlExecutor,
  },
};

export function getTool(name: string): ToolDefinition | undefined {
  return TOOLS[name];
}

export function realTools(): ToolDefinition[] {
  return Object.values(TOOLS).filter((t) => !!t.execute);
}
